import React, { useRef, useState } from 'react';
import { ROLES, RoleKey } from './roleContent';

/**
 * "Kimsin?" — sayfanın tek etkileşimli anı. Rol değişince maskotun pozu ve o
 * role ait özellik listesi değişir.
 *
 * Erişilebilirlik: ARIA tab kalıbı. Tek tab durağı (roving tabindex), ok tuşları
 * + Home/End ile gezinme, seçim odakla birlikte değişir (üç sekme olduğu için
 * "otomatik etkinleştirme" doğru seçim).
 *
 * Üç poz da DOM'da yığılı durur ve opaklıkla değişir: sadece seçili olanı
 * yüklesek, ilk geçişte ~400 KB'lık PNG gelene kadar sahne boş görünürdü.
 */
const RoleSwitcher: React.FC = () => {
  const [role, setRole] = useState<RoleKey>('ogrenci');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const active = ROLES.find((r) => r.key === role) ?? ROLES[0];

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = ROLES.length - 1;
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next < 0) return;
    e.preventDefault();
    const target = ROLES[next];
    setRole(target.key);
    tabRefs.current[target.key]?.focus();
  };

  return (
    <section className="lp-section lp-roles" aria-labelledby="lp-roles-title">
      <div className="lp-wrap">
        <div className="lp-roles__head">
          <h2 id="lp-roles-title" className="lp-h2">
            Kimsin?
          </h2>
          <div className="lp-tabs" role="tablist" aria-label="Rolünü seç">
            {ROLES.map((r, i) => (
              <button
                key={r.key}
                ref={(el) => {
                  tabRefs.current[r.key] = el;
                }}
                type="button"
                role="tab"
                id={`lp-tab-${r.key}`}
                aria-selected={r.key === role}
                aria-controls="lp-role-panel"
                tabIndex={r.key === role ? 0 : -1}
                className="lp-tab"
                onClick={() => setRole(r.key)}
                onKeyDown={(e) => onKeyDown(e, i)}
              >
                {r.tab}
              </button>
            ))}
          </div>
        </div>

        <div
          id="lp-role-panel"
          role="tabpanel"
          aria-labelledby={`lp-tab-${role}`}
          tabIndex={0}
          className="lp-stage"
        >
          <div className="lp-stage__pose">
            {ROLES.map((r) => (
              <img
                key={r.key}
                src={r.image}
                alt={r.alt}
                aria-hidden={r.key === role ? undefined : true}
                className={`lp-pose${r.key === role ? ' is-on' : ''}`}
                width={770}
                height={778}
                decoding="async"
                draggable={false}
              />
            ))}
          </div>

          <div className="lp-stage__body" key={role}>
            <p className="lp-lead">{active.lead}</p>
            <ul className="lp-feats">
              {active.features.map((f) => (
                <li key={f.title} className="lp-feat">
                  <span className="lp-feat__chip" style={{ backgroundColor: f.color }} aria-hidden>
                    <img src={`/sidebar-icons/${f.icon}.svg`} alt="" />
                  </span>
                  <div>
                    <h3 className="lp-feat__title">{f.title}</h3>
                    <p className="lp-feat__text">{f.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoleSwitcher;
